/**
 * Email Verification Types
 * TypeScript types for email verification feature
 */

/**
 * Email 驗證請求參數
 */
export interface VerifyEmailRequest {
  /** 驗證 token (64 字符 hex string) */
  token: string
}

/**
 * Email 驗證成功響應
 */
export interface VerifyEmailSuccessResponse {
  success: true
  message: string
}

/**
 * Email 驗證錯誤響應
 */
export interface VerifyEmailErrorResponse {
  error: string
  code: VerificationErrorCode
}

/**
 * 驗證錯誤代碼
 */
export type VerificationErrorCode =
  | 'INVALID_TOKEN' // Token 格式無效或不存在
  | 'TOKEN_EXPIRED' // Token 已過期 (> 1 hour)
  | 'TOKEN_USED' // Token 已被使用
  | 'VERIFICATION_FAILED' // 其他驗證失敗原因

/**
 * 驗證結果狀態
 */
export type VerificationStatus = 'idle' | 'loading' | 'success' | 'error'

/**
 * 重新發送驗證郵件成功響應
 */
export interface ResendEmailSuccessResponse {
  success: true
  message: string
  expiresIn: number // Token 有效期 (秒), e.g., 3600
}

/**
 * 重新發送郵件錯誤響應
 */
export interface ResendEmailErrorResponse {
  success: false
  message: string
  code: ResendErrorCode
  retryAfter?: number // 速率限制時才有,表示剩餘等待秒數
}

/**
 * 重新發送錯誤代碼
 */
export type ResendErrorCode =
  | 'TOO_FREQUENT' // 速率限制 (1 分鐘內重複請求)
  | 'ALREADY_VERIFIED' // Email 已驗證
  | 'SEND_FAILED' // Email 發送失敗 (服務錯誤)

/**
 * 用戶 Email 驗證狀態響應
 */
export interface EmailVerificationStatusResponse {
  email: string
  verified: boolean
}

/**
 * VerifyEmailPage 組件狀態
 */
export interface VerifyEmailPageState {
  status: VerificationStatus
  errorCode: VerificationErrorCode | null
  errorMessage: string | null
  token: string | null
}

/**
 * EmailVerificationBanner 組件狀態
 */
export interface EmailVerificationBannerState {
  isVisible: boolean // 是否顯示 banner
  isResending: boolean // 是否正在重新發送郵件
  countdown: number | null // 速率限制倒數計時 (秒)
}

/**
 * ResendEmailButton 組件 props
 */
export interface ResendEmailButtonProps {
  /** 是否正在發送 */
  isLoading: boolean
  /** 速率限制倒數 (秒), null 表示可以發送 */
  countdown: number | null
  /** 點擊處理函數 */
  onClick: () => void
  /** 按鈕樣式變體 */
  variant?: 'primary' | 'secondary'
  /** 自訂 className */
  className?: string
}

/**
 * Check if response is verification error
 */
export function isVerificationError(
  response: VerifyEmailSuccessResponse | VerifyEmailErrorResponse
): response is VerifyEmailErrorResponse {
  return 'error' in response && 'code' in response
}

/**
 * Check if response is resend error
 */
export function isResendError(
  response: ResendEmailSuccessResponse | ResendEmailErrorResponse
): response is ResendEmailErrorResponse {
  return 'success' in response && response.success === false
}

/**
 * Check if error has retry info (rate limiting)
 */
export function hasRetryAfter(
  error: ResendEmailErrorResponse
): error is ResendEmailErrorResponse & { retryAfter: number } {
  return error.code === 'TOO_FREQUENT' && typeof error.retryAfter === 'number'
}

/**
 * Error message mapping (Chinese)
 */
export const ERROR_MESSAGES: Record<
  VerificationErrorCode | ResendErrorCode,
  string
> = {
  // Verification errors
  INVALID_TOKEN: '驗證連結無效,請檢查連結是否完整',
  TOKEN_EXPIRED: '驗證連結已過期,請重新發送驗證郵件',
  TOKEN_USED: '此驗證連結已使用,您的 Email 已驗證',
  VERIFICATION_FAILED: '驗證失敗,請稍後再試或聯繫客服',

  // Resend errors
  TOO_FREQUENT: '請稍後再試',
  ALREADY_VERIFIED: '您的 Email 已驗證',
  SEND_FAILED: 'Email 發送失敗,請稍後再試',
}
